import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Services', href: '/services' },
  { name: 'Commercial', href: '/commercial' },
  { name: 'Medical', href: '/medical' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'About', href: '/about' },
  { name: 'Blog', href: '/blog' },
  { name: 'Testimonials', href: '/testimonials' },
  { name: 'Contact', href: '/contact' },
];

function Logo() {
  return (
    <Link to="/" aria-label="Sonz of Thunder Services - Home" className="flex items-center gap-3 group">
      <span className="logo-badge logo-badge--nav transition-transform group-hover:scale-105">
        <img src="/images/sotsvc-logo.png" alt="SOTSVC — Sonz of Thunder Services" />
      </span>
      <div className="leading-none">
        <div className="font-display font-bold text-white text-base md:text-lg tracking-wider">SONZ OF THUNDER</div>
        <div className="text-electric text-[10px] tracking-[0.35em] mt-1">SERVICES</div>
      </div>
    </Link>
  );
}

export { Logo };

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleQuoteClick = () => {
    window.dispatchEvent(new CustomEvent('openContactForm'));
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-charcoal-deep/95 backdrop-blur-md border-b border-electric/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Logo size={44} />

        <nav className="hidden xl:flex items-center gap-6">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`font-display text-sm tracking-[0.15em] transition relative group ${
                location.pathname === item.href
                  ? 'text-thunder'
                  : 'text-white/80 hover:text-thunder'
              }`}
            >
              {item.name}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-thunder group-hover:w-full transition-all" />
            </Link>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-4">
          <a
            href="tel:4074616039"
            className="font-display text-white tracking-wider hover:text-thunder text-sm transition"
          >
            (407) 461-6039
          </a>
          <button
            onClick={handleQuoteClick}
            className="bg-electric hover:bg-electric-bright text-charcoal-deep font-display font-bold px-6 py-3 tracking-[0.2em] text-xs transition skew-card"
          >
            <span className="skew-inner block">FREE QUOTE</span>
          </button>
        </div>

        <button
          onClick={() => setIsOpen(!isOpen)}
          className="xl:hidden text-thunder p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
            {isOpen ? (
              <path d="M6 6l12 12M6 18L18 6" />
            ) : (
              <path d="M4 7h16M4 12h16M4 17h16" />
            )}
          </svg>
        </button>
      </div>

      {isOpen && (
        <div className="xl:hidden bg-charcoal-deep/98 backdrop-blur-md border-t border-electric/20 fade-in">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`font-display tracking-[0.2em] text-sm py-3 min-h-[44px] flex items-center ${
                  location.pathname === item.href ? 'text-thunder' : 'text-white'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <a
              href="tel:4074616039"
              className="text-thunder font-display tracking-wider pt-3 border-t border-white/10 min-h-[44px] flex items-center"
            >
              (407) 461-6039
            </a>
            <button
              onClick={() => {
                setIsOpen(false);
                handleQuoteClick();
              }}
              className="bg-electric text-charcoal-deep font-display font-bold px-6 py-3 tracking-[0.2em] text-xs text-center"
            >
              GET FREE QUOTE
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
